import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { PATHS } from '@constants/paths';
import { LoadingFallback } from '@shared/components/ui/LoadingFallback';
import { useCurrentUser } from '@shared/hooks';
import { LazyNotFound, LazyUserDetails } from './lazyComponents';

// Feature route maps — each feature owns its permission-to-component mapping
import { analystPages } from '@features/analyst/routes';
import { bankAdminPages } from '@features/bankAdmin/routes';
import { cashierPages } from '@features/cashier/routes';
import { correspondentPages } from '@features/correspondent/routes';
import { managerPages } from '@features/manager/routes';
import { reportsPages } from '@features/reports/routes';
import { HomePage, subpagesPages } from '@features/subpages/routes';

// ---------------------------------------------------------------------------
// NestedRouting
// ---------------------------------------------------------------------------
const NestedRouting = () => {
  const user = useCurrentUser();
  const { permissions = [] as UserPermission[] } = user;

  const pages = (displayName: string): Record<string, React.ReactNode> => ({
    // Feature page maps
    ...cashierPages(displayName),
    ...analystPages(displayName),
    ...correspondentPages(displayName),
    ...managerPages(displayName),
    ...bankAdminPages(displayName),
    ...reportsPages(displayName),
    ...subpagesPages(displayName),

    // Shared pages (no feature ownership)
    profile: <LazyUserDetails />,

    // Placeholders de texto hasta asignar vistas reales a cada permiso To_DO
    cashierTransactions: 'component for cashierTransactions here',
    correspondentFeeReport: 'component for correspondentFeeReport here',
    correspondentTransactionsSubsidiary: 'component for correspondentTransactionsSubsidiary here',
    correspondentParameters: 'component for correspondentParameters here',
    createCorrespondentUser: 'component for createCorrespondentUser here',
    deleteCorrespondentUser: 'component for deleteCorrespondentUser here',
    editCorrespondentUser: 'component for editCorrespondentUser here',
    correspondentAudithReport: 'component for correspondentAudithReport here',
  });

  interface UserPermission {
    name: string;
    path: string;
    display_name: string;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path={PATHS.HOME} element={<HomePage />} />
        <Route path={`${PATHS.HOME}#/`} element={<HomePage />} />
        <Route path="/auth/login/*" element={<Navigate to="/" replace />} />
        <Route path={'/'} element={<HomePage />} />
        {permissions.map(({ name, path, display_name: displayName }: UserPermission) => {
          const routeKey = `${path}-${String(name)}`;
          if (path === '/correspondentConciliation') {
            return <Route path={`${path}/*`} key={routeKey} element={pages(displayName)[name]} />;
          }
          return <Route path={path} key={routeKey} element={pages(displayName)[name]} />;
        })}
        <Route path="/*" element={<LazyNotFound />} />
      </Routes>
    </Suspense>
  );
};

export default NestedRouting;
